import type { PriceId } from '@iheartjane/types';

import type { AlgoliaPriceId } from '@jane/search/data-access';
import { maybeParseFloat } from '@jane/shared/util';

import type { CartRowProduct } from './cartRowProduct';

interface CartRowPrice {
  discountPrice: number | undefined;
  priceId: PriceId;
  unitPrice: number | undefined;
}

/**
 * carts-v3 rule 3: compute a cart row's display prices by READING the
 * backend-precomputed `price_{id}` / `discounted_price_{id}` fields, instead of
 * routing the product through the io-ts-typed `useGetProductPrices` hook.
 *
 * `maybeParseFloat` matches the retired `getUnitPrice` / `getDiscountedUnitPrice`
 * coercion exactly (v1 ships strings, v3 ships numbers; `''` and `null` →
 * `undefined`).
 *
 * The discount is gated on `isSupportedSpecial` — the cart line's applied store
 * special (`special_id`) is in the supported set. This is the cart's discount
 * signal and the ONLY one a v3 CartProduct carries: v3 has no `special_price_{id}`
 * per-weight field (that legacy menu-browse branch, also consulted by the old
 * `isDiscountSupported`, does not apply to cart rows, which surface their applied
 * special via the top-level `special_id`).
 *
 * See docs/specs/carts-v3/2026-06-11-migration-map-design.md.
 */
export const cartRowPrice = (
  product: CartRowProduct,
  priceId: AlgoliaPriceId | PriceId | undefined,
  isSupportedSpecial: boolean
): CartRowPrice => {
  // AlgoliaPriceId is space-separated; PriceId / cart data is underscore form.
  const resolvedPriceId = (priceId ?? product.price_id)
    .split(' ')
    .join('_') as PriceId;
  const unitPrice = maybeParseFloat(product[`price_${resolvedPriceId}`]);
  const discountPrice = isSupportedSpecial
    ? maybeParseFloat(product[`discounted_price_${resolvedPriceId}`])
    : undefined;

  return { discountPrice, priceId: resolvedPriceId, unitPrice };
};
